import React, { useEffect, useState, useCallback } from 'react';
import { useSpaces } from '../contexts';
import { KatSpinner, KatButton } from '@amzn/katal-react';
import NewSpaceDialog from './NewSpaceDialog';
import SpaceStatusIndicator from './SpaceStatusIndicator';

/**
 * Dashboard component - main view for Rufus Spaces
 *
 * Displays list of user's spaces with status indicators.
 * Supports creating new spaces, viewing/editing existing spaces.
 */
const Dashboard: React.FC = () => {
    const { state, fetchSpaces } = useSpaces();
    const { spaces, loading, error } = state;
    const [showNewSpaceDialog, setShowNewSpaceDialog] = useState(false);

    // Handlers for new space dialog
    const handleOpenNewSpace = useCallback(() => {
        setShowNewSpaceDialog(true);
    }, []);

    const handleCloseNewSpace = useCallback(() => {
        setShowNewSpaceDialog(false);
    }, []);

    // Fetch spaces on mount
    useEffect(() => {
        fetchSpaces();
    }, [fetchSpaces]);

    if (loading && spaces.length === 0) {
        return (
            <div className="dashboard-loading">
                <KatSpinner size="large" />
                <p>Loading spaces...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-error">
                <h2>Error loading spaces</h2>
                <p>{error}</p>
                <button onClick={() => fetchSpaces()}>Retry</button>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <header className="dashboard-header">
                <div className="dashboard-header-content">
                    <h1>Rufus Spaces</h1>
                    <p>Create, edit, and deploy Streamlit applications</p>
                </div>
                <div className="dashboard-header-actions">
                    <KatButton
                        variant="primary"
                        label="Create New Space"
                        onClick={handleOpenNewSpace}
                    />
                </div>
            </header>

            <main className="dashboard-content">
                {spaces.length === 0 ? (
                    <div className="dashboard-empty">
                        <h2>No spaces yet</h2>
                        <p>Create your first space to get started.</p>
                        <KatButton
                            variant="primary"
                            label="Create Your First Space"
                            onClick={handleOpenNewSpace}
                        />
                    </div>
                ) : (
                    <div className="spaces-list">
                        <h2>Your Spaces ({spaces.length})</h2>
                        <ul>
                            {spaces.map((space) => (
                                <li key={space.spaceId} className="space-item">
                                    <span className="space-name">{space.name}</span>
                                    <SpaceStatusIndicator status={space.status} />
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </main>

            <NewSpaceDialog
                visible={showNewSpaceDialog}
                onClose={handleCloseNewSpace}
            />
        </div>
    );
};

export default Dashboard;
